import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  FormGroup,
  Label,
} from "reactstrap";
import { GetData, PostData } from "../../services/api";
import Swal from "sweetalert2";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toSlug = (str) =>
  (str || "").toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_-]/g, "").slice(0, 40) || "landing_page";

export function extractLpFormConfig(html) {
  try {
    const doc = new DOMParser().parseFromString(html || "", "text/html");
    const s = doc.querySelector("script#lp-form-config");
    if (!s) return null;
    const m = s.textContent.match(/window\.__LP_FORM__\s*=\s*(\{[\s\S]*?\});/);
    if (!m) return null;
    // eslint-disable-next-line no-new-func
    return new Function("return " + m[1])();
  } catch (_) {
    return null;
  }
}

const masked = (k) => k.slice(0, 8) + "••••••••••••••••" + k.slice(-4);

// SweetAlert2 inside a z-index:10001 modal needs its container pushed above it
const swal = (opts) =>
  Swal.fire({
    ...opts,
    didOpen: () => {
      const c = Swal.getContainer();
      if (c) c.style.zIndex = "20000";
    },
  });

// ─── Component ──────────────────────────��─────────────────────────────────────

const FormConfigModal = ({ isOpen, toggle, page, onApply }) => {
  const [contactLists, setContactLists]   = useState([]);
  const [selectedListId, setSelectedListId] = useState("");
  const [keyId, setKeyId]               = useState(null);
  const [apiKey, setApiKey]             = useState(null);
  const [loadingKey, setLoadingKey]     = useState(false);
  const [generating, setGenerating]     = useState(false);
  const [revealed, setRevealed]         = useState(false);
  const [createdBy, setCreatedBy]       = useState("");
  const [redirectUrl, setRedirectUrl]   = useState("");
  const [applying, setApplying]         = useState(false);

  const apiBase  = process.env.REACT_APP_API || "http://127.0.0.1:5001";
  const endpoint = `${apiBase}/integration/contacts`;

  // ── Fetch key for a list ────────────────────────────────────────────────────

  const fetchKey = useCallback(async (listId) => {
    if (!listId) return;
    setLoadingKey(true);
    try {
      const res = await GetData(`/integration/api-keys/${listId}`);
      setApiKey(res?.data?.apiKey || null);
      setKeyId(res?.data?.keyId || null);
    } catch (_) {
      setApiKey(null);
      setKeyId(null);
    } finally {
      setLoadingKey(false);
    }
  }, []);

  // ── Open: load lists + existing config ─────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    setRevealed(false);

    // Load contact lists
    GetData("/contact-lists")
      .then((res) => setContactLists(res?.data || []))
      .catch(() => setContactLists([]));

    // Try to pre-populate from existing inline config
    const existing = extractLpFormConfig(page?.content?.html);
    if (existing) {
      const listId = existing.contactListId || page?.contactListId || "";
      setSelectedListId(listId);
      setCreatedBy(existing.createdBy || toSlug(page?.name));
      setRedirectUrl(existing.redirectUrl || page?.redirectUrl || "");
      if (listId) fetchKey(listId);
    } else {
      setSelectedListId(page?.contactListId || "");
      setCreatedBy(toSlug(page?.name));
      setRedirectUrl(page?.redirectUrl || "");
      if (page?.contactListId) fetchKey(page.contactListId);
    }
  }, [isOpen, page, fetchKey]);

  // ── List change ─────────────────────────────────────────────────────────────

  const handleListChange = (e) => {
    const listId = e.target.value;
    setSelectedListId(listId);
    setApiKey(null);
    setKeyId(null);
    setRevealed(false);
    if (!createdBy) setCreatedBy(toSlug(page?.name));
    if (listId) fetchKey(listId);
  };

  // ── Generate / regenerate key ───────────────────────────────────────────────

  const handleGenerate = async () => {
    const isRegen = !!apiKey;
    const ok = await swal({
      title: isRegen ? "¿Regenerar API Key?" : "Generar API Key",
      text: isRegen
        ? "La clave anterior dejará de funcionar de inmediato."
        : "Se generará una clave para esta lista.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#6C63FF",
      cancelButtonColor: "#6c757d",
      confirmButtonText: isRegen ? "Sí, regenerar" : "Generar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    setGenerating(true);
    try {
      const res = await PostData(`/integration/api-keys/${selectedListId}`, {});
      setApiKey(res?.data?.apiKey || null);
      setKeyId(res?.data?.keyId || null);
      setRevealed(true);
    } catch (_) {
      swal({ icon: "error", title: "Error", text: "No se pudo generar la API Key" });
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text) =>
    navigator.clipboard
      .writeText(text)
      .then(() =>
        swal({ icon: "success", title: "¡Copiado!", timer: 900, showConfirmButton: false })
      );

  // ── Apply ───────────────────────────────────────────────────────────────────

  const handleApply = async () => {
    if (!selectedListId || !apiKey) return;
    setApplying(true);
    try {
      await onApply({ contactListId: selectedListId, apiKey, endpoint, createdBy, redirectUrl });
    } finally {
      setApplying(false);
    }
  };

  const canApply = !!selectedListId && !!apiKey && !applying;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" zIndex={10001}>
      <ModalHeader toggle={toggle}>
        <i className="mdi mdi-link-variant me-2" />
        Configurar formulario
      </ModalHeader>

      <ModalBody>
        {/* Endpoint info */}
        <div
          className="p-3 rounded mb-4"
          style={{ background: "#0d0d1a", border: "1px solid #2a2a4a" }}
        >
          <div style={{ fontSize: 11, color: "#6c757d", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>
            Endpoint (POST)
          </div>
          <code style={{ fontSize: 12, color: "#a8b5d8", wordBreak: "break-all" }}>{endpoint}</code>
          <div style={{ marginTop: 6, fontSize: 11, color: "#555" }}>
            El HTML exportado es 100% autocontenido — funciona en cualquier servidor estático.
          </div>
        </div>

        {/* Contact list */}
        <FormGroup>
          <Label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
            Lista de contactos destino
          </Label>
          <Input
            type="select"
            value={selectedListId}
            onChange={handleListChange}
          >
            <option value="">— Selecciona una lista —</option>
            {contactLists.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </Input>
        </FormGroup>

        {/* API key */}
        <FormGroup>
          <Label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
            API Key <span style={{ fontWeight: 400 }}>(header X-API-Key — se embebe en el HTML)</span>
          </Label>

          {!selectedListId ? (
            <p className="text-muted" style={{ fontSize: 13 }}>Selecciona una lista primero.</p>
          ) : loadingKey ? (
            <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <span className="spinner-border spinner-border-sm" style={{ width: "0.75rem", height: "0.75rem" }} />
              Verificando...
            </div>
          ) : apiKey ? (
            <>
              <InputGroup>
                <Input
                  readOnly
                  value={revealed ? apiKey : masked(apiKey)}
                  style={{ fontFamily: "monospace", fontSize: 13 }}
                />
                <Button color="secondary" outline size="sm" onClick={() => setRevealed((v) => !v)} title={revealed ? "Ocultar" : "Mostrar"}>
                  <i className={`mdi ${revealed ? "mdi-eye-off" : "mdi-eye"}`} />
                </Button>
                <Button color="secondary" outline size="sm" onClick={() => copy(apiKey)} title="Copiar">
                  <i className="mdi mdi-content-copy" />
                </Button>
                <Button color="warning" outline size="sm" onClick={handleGenerate} disabled={generating} title="Regenerar (desactiva la clave actual)">
                  <i className="mdi mdi-refresh" />
                </Button>
              </InputGroup>
              <small className="text-muted">
                Clave exclusiva de esta lista. Regenerar desactiva la anterior.
              </small>
            </>
          ) : (
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted" style={{ fontSize: 13 }}>No hay API Key para esta lista.</span>
              <Button color="primary" size="sm" onClick={handleGenerate} disabled={generating}>
                {generating ? (
                  <><span className="spinner-border spinner-border-sm me-1" style={{ width: "0.7rem", height: "0.7rem" }} />Generando...</>
                ) : (
                  <><i className="mdi mdi-key-plus me-1" />Generar API Key</>
                )}
              </Button>
            </div>
          )}
        </FormGroup>

        {/* createdBy */}
        <FormGroup>
          <Label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
            createdBy <span style={{ fontWeight: 400 }}>(identifica la fuente en el CRM)</span>
          </Label>
          <Input
            value={createdBy}
            onChange={(e) => setCreatedBy(e.target.value)}
            placeholder="ej: webinar_julio, landing_principal"
            style={{ fontFamily: "monospace", fontSize: 13 }}
          />
        </FormGroup>

        {/* redirectUrl */}
        <FormGroup className="mb-0">
          <Label style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6c757d" }}>
            URL de redirección <span style={{ fontWeight: 400 }}>(tras envío exitoso)</span>
          </Label>
          <Input
            value={redirectUrl}
            onChange={(e) => setRedirectUrl(e.target.value)}
            placeholder="https://tu-sitio.com/gracias"
            style={{ fontSize: 13 }}
          />
          <small className="text-muted">Opcional. Si se deja vacío, el botón mostrará "¡Registrado! ✓".</small>
        </FormGroup>
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" outline onClick={toggle}>
          Cancelar
        </Button>
        <Button
          color="primary"
          onClick={handleApply}
          disabled={!canApply}
          style={{ background: canApply ? "#6C63FF" : undefined, borderColor: canApply ? "#6C63FF" : undefined }}
        >
          {applying ? (
            <><span className="spinner-border spinner-border-sm me-1" style={{ width: "0.75rem", height: "0.75rem" }} />Guardando...</>
          ) : (
            <><i className="mdi mdi-check me-1" />Aplicar y guardar</>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FormConfigModal;
